import {
  CONCENTRATION_RANKS,
  FIC_FIDC_COCKPIT_CONCENTRATION_VERSION,
} from './contract';

import type {
  ConcentrationRank,
  FicFidcCockpitConcentrationV1Payload,
  FicFidcCockpitConcentrationV1Point,
  FicFidcCockpitConcentrationV1Position,
  FicFidcCompositionCardViewModel,
} from './contract';

/**
 * Estados de exibição dos widgets de concentração. O backend reporta status e
 * razão; aqui a razão é elevada a estado quando altera a mensagem exibida
 * (zero-total e no-competence-data), mantendo a distinção pedida pelos
 * estados: ready, partial, empty, no-competence-data, unavailable e
 * zero-total. `loading` e `error` são estados do consumo, não do payload.
 */
export type FicFidcCockpitConcentrationStatus =
  | 'ready'
  | 'partial'
  | 'empty'
  | 'no-competence-data'
  | 'unavailable'
  | 'zero-total'
  | 'error';

export interface FicFidcCockpitConcentrationRankEntry {
  rank: ConcentrationRank;
  /** Soma acumulada (R$) dos `rank` maiores FIDCs; null quando N/D. */
  value: number | null;
  /** Participação acumulada (%) sobre o PL do pai; null quando N/D. */
  percentage: number | null;
  /** true quando o backend reportou o card como disponível. */
  isAvailable: boolean;
  /** true quando a carteira não tem FIDC elegível até a posição do rank. */
  isMissingPosition: boolean;
}

export interface FicFidcCockpitConcentrationFundSlice {
  fundName: string;
  /** Valor (R$) reportado pelo backend para a fatia. */
  value: number;
  /** Participação (%) reportada pelo backend sobre o PL do pai. */
  percentage: number;
}

export interface FicFidcCockpitConcentrationCurvePoint {
  /** Posição do fundo na ordenação do backend (1 = maior). */
  rank: number;
  /** Valor financeiro acumulado reportado pelo backend para a posição. */
  cumulativeVlMercPosFinal: number;
  /** Participação acumulada (%) reportada pelo backend para a posição. */
  cumulativePercentage: number;
}

export interface FicFidcCockpitConcentrationData {
  status: FicFidcCockpitConcentrationStatus;
  reason: FicFidcCockpitConcentrationV1Payload['meta']['reason'];
  /** Competência efetiva reportada pelo backend; null sem dados. */
  competence: string | null;
  /** FIDCs elegíveis na competência (coverage.eligibleFundCount). */
  totalFunds: number | null;
  /** PL do FIC pai (denominador autoritativo; meta.parent.vlPatrimLiq). */
  totalValue: number | null;
  parentCnpj: string | null;
  missingPositionCount: number | null;
  ranks: FicFidcCockpitConcentrationRankEntry[];
  funds: FicFidcCockpitConcentrationFundSlice[];
  /**
   * Curva acumulada do gráfico resumido por fundo: participações acumuladas
   * (%) JÁ reportadas pelo backend, uma por posição elegível. Vazia nos
   * estados indisponíveis.
   */
  curve: FicFidcCockpitConcentrationCurvePoint[];
  composition: {
    asset: FicFidcCompositionCardViewModel;
    manager: FicFidcCompositionCardViewModel;
  };
}

const unavailableCompositionCard = (
  key: 'asset' | 'manager',
  competence: string | null = null,
): FicFidcCompositionCardViewModel => ({
  key,
  label: key === 'asset' ? 'Ativo-lastro' : 'Gestor',
  amount: null,
  percentage: null,
  status: 'unavailable',
  reason: 'missing-metadata',
  competence,
  coverage: { total: 0, withMetadata: 0 },
  items: [],
});

function mapGroupedComposition(
  key: 'asset' | 'manager',
  grouped: FicFidcCockpitConcentrationV1Payload['meta']['grouped'],
  competence: string | null,
): FicFidcCompositionCardViewModel {
  const source = grouped?.[key === 'asset' ? 'ativoLastro' : 'gestor'];

  if (!source) return unavailableCompositionCard(key, competence);

  return {
    key,
    label: key === 'asset' ? 'Ativo-lastro' : 'Gestor',
    amount: source.data[0]?.vlMercPosFinal ?? null,
    percentage: source.data[0]?.percentage ?? null,
    status: source.meta.status,
    reason: source.meta.reason,
    competence: source.meta.competence,
    coverage: {
      total: source.meta.coverage.fundCount,
      withMetadata:
        source.meta.coverage.fundCount -
        source.meta.coverage.missingMetadataCount,
    },
    items: source.data,
  };
}

function isGroupedCompositionValid(value: unknown): boolean {
  if (value === null || typeof value !== 'object') return false;

  const composition = value as Record<string, unknown>;
  const meta = composition.meta;
  const data = composition.data;

  if (!Array.isArray(data) || meta === null || typeof meta !== 'object') {
    return false;
  }

  const compositionMeta = meta as Record<string, unknown>;
  const coverage = compositionMeta.coverage;

  if (
    !KNOWN_STATUSES.includes(
      compositionMeta.status as (typeof KNOWN_STATUSES)[number],
    ) ||
    !KNOWN_REASONS.includes(
      compositionMeta.reason as (typeof KNOWN_REASONS)[number],
    ) ||
    (compositionMeta.competence !== null &&
      typeof compositionMeta.competence !== 'string') ||
    coverage === null ||
    typeof coverage !== 'object'
  ) {
    return false;
  }

  const compositionCoverage = coverage as Record<string, unknown>;

  if (
    !isNonNegativeInteger(compositionCoverage.fundCount) ||
    !isNonNegativeInteger(compositionCoverage.eligibleFundCount) ||
    !isNonNegativeInteger(compositionCoverage.missingPositionCount) ||
    !isNonNegativeInteger(compositionCoverage.missingMetadataCount)
  ) {
    return false;
  }

  return data.every((item) => {
    if (item === null || typeof item !== 'object') return false;

    const groupedItem = item as Record<string, unknown>;

    return (
      typeof groupedItem.name === 'string' &&
      isNonNegativeInteger(groupedItem.fundCount) &&
      toNonNegativeNumber(groupedItem.vlMercPosFinal) !== null &&
      toNonNegativeNumber(groupedItem.percentage) !== null
    );
  });
}

const KNOWN_STATUSES = [
  'available',
  'partial',
  'empty',
  'unavailable',
  'error',
] as const;
const KNOWN_REASONS = [
  'none',
  'empty',
  'missing-position',
  'zero-total',
  'no-competence-data',
  'missing-metadata',
  'source-error',
] as const;

function isConcentrationRank(value: number): value is ConcentrationRank {
  return (CONCENTRATION_RANKS as readonly number[]).includes(value);
}

const UNAVAILABLE_RESULT_STATUSES: ReadonlySet<string> = new Set([
  'empty',
  'no-competence-data',
  'unavailable',
  'zero-total',
  'error',
]);

/** Limite de fatias nomeadas exibidas no gráfico resumido por fundo. */
export const FUND_CHART_SLICE_LIMIT = 5;

const OTHER_SLICE_NAME = 'Outros';

/**
 * Aceita apenas os formatos explicitamente suportados: número finito ou
 * string numérica não vazia em formato decimal. Coerções implícitas de
 * `Number(value)` são rejeitadas — strings vazias/só de espaços e booleanos
 * converteriam para 0/1, mascarando campo ausente/malformado como valor
 * financeiro válido; booleans/arrays/objetos também não são numéricos.
 */
const DECIMAL_NUMBER_PATTERN = /^(?:[+-]?(?:\d+(?:\.\d+)?|\.\d+))$/;

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') return null;

  const trimmedValue = value.trim();

  if (trimmedValue.length === 0 || !DECIMAL_NUMBER_PATTERN.test(trimmedValue)) {
    return null;
  }

  const numericValue = Number(trimmedValue);

  return Number.isFinite(numericValue) ? numericValue : null;
}

function toNonNegativeNumber(value: unknown): number | null {
  const numericValue = toFiniteNumber(value);

  return numericValue === null || numericValue < 0 ? null : numericValue;
}

function isNonNegativeInteger(value: unknown): boolean {
  const numericValue = toFiniteNumber(value);

  return (
    numericValue !== null && Number.isInteger(numericValue) && numericValue >= 0
  );
}

/**
 * Normalização no limite do mapper: o contrato declara campos numéricos, mas
 * payloads de rede podem chegar com números serializados como string. Cada
 * campo é normalizado para number ANTES de qualquer soma/agregação — sem
 * normalização, `+` concatena strings e propaga valores financeiros errados.
 * Campos obrigatórios inválidos produzem null; campos opcionais inválidos
 * produzem undefined (null legítimo do contrato é preservado). O mapper
 * degrada para unavailable quando algo não normaliza, sem fallback silencioso.
 */
function normalizeRequiredInteger(value: unknown): number | null {
  const numericValue = toFiniteNumber(value);

  return numericValue !== null &&
    Number.isInteger(numericValue) &&
    numericValue >= 0
    ? numericValue
    : null;
}

function normalizeRequiredNonNegative(value: unknown): number | null {
  return toNonNegativeNumber(value);
}

/** undefined marca campo numérico inválido; null do contrato é mantido. */
function normalizeOptionalNonNegative(
  value: unknown,
): number | null | undefined {
  if (value === null) return null;

  const numericValue = toNonNegativeNumber(value);

  return numericValue === null ? undefined : numericValue;
}

/** undefined marca campo numérico inválido; null do contrato é mantido. */
function normalizeOptionalFinite(value: unknown): number | null | undefined {
  if (value === null) return null;

  const numericValue = toFiniteNumber(value);

  return numericValue === null ? undefined : numericValue;
}

function normalizePositionNumbers(
  position: FicFidcCockpitConcentrationV1Position,
): FicFidcCockpitConcentrationV1Position | null {
  if (position === null || typeof position !== 'object') return null;

  const rank = normalizeRequiredInteger(position.rank);
  const qtPosFinal = normalizeOptionalFinite(position.qtPosFinal);
  const vlMercPosFinal = normalizeRequiredNonNegative(position.vlMercPosFinal);
  const percentage = normalizeOptionalNonNegative(position.percentage);
  const cumulativeVlMercPosFinal = normalizeRequiredNonNegative(
    position.cumulativeVlMercPosFinal,
  );
  const cumulativePercentage = normalizeOptionalNonNegative(
    position.cumulativePercentage,
  );

  if (
    rank === null ||
    qtPosFinal === undefined ||
    percentage === undefined ||
    vlMercPosFinal === null ||
    cumulativeVlMercPosFinal === null ||
    cumulativePercentage === undefined
  ) {
    return null;
  }

  return {
    ...position,
    rank,
    qtPosFinal,
    vlMercPosFinal,
    percentage,
    cumulativeVlMercPosFinal,
    cumulativePercentage,
  };
}

function normalizePointNumbers(
  point: FicFidcCockpitConcentrationV1Point,
): FicFidcCockpitConcentrationV1Point | null {
  if (point === null || typeof point !== 'object') return null;

  const top = normalizeRequiredInteger(point.top);

  if (top === null || !isConcentrationRank(top)) return null;

  if (!point.available) return { ...point, top };

  const rank = normalizeRequiredInteger(point.rank);
  const cumulativeVlMercPosFinal = normalizeRequiredNonNegative(
    point.cumulativeVlMercPosFinal,
  );
  const cumulativePercentage = normalizeRequiredNonNegative(
    point.cumulativePercentage,
  );

  if (
    rank === null ||
    cumulativeVlMercPosFinal === null ||
    cumulativePercentage === null
  ) {
    return null;
  }

  return {
    ...point,
    top,
    rank,
    cumulativeVlMercPosFinal,
    cumulativePercentage,
  };
}

function normalizePayloadNumbers(
  payload: FicFidcCockpitConcentrationV1Payload | null | undefined,
): FicFidcCockpitConcentrationV1Payload | null {
  if (payload === null || payload === undefined || typeof payload !== 'object')
    return null;

  const { meta } = payload as FicFidcCockpitConcentrationV1Payload;

  if (meta === null || typeof meta !== 'object') return null;

  const { parent, coverage, cards, markers, ...metaRest } = meta;

  if (
    !Array.isArray(payload.data) ||
    parent === null ||
    typeof parent !== 'object' ||
    coverage === null ||
    typeof coverage !== 'object' ||
    cards === null ||
    typeof cards !== 'object' ||
    !Array.isArray(markers)
  ) {
    return null;
  }

  const fundCount = normalizeRequiredInteger(coverage.fundCount);
  const eligibleFundCount = normalizeRequiredInteger(
    coverage.eligibleFundCount,
  );
  const missingPositionCount = normalizeRequiredInteger(
    coverage.missingPositionCount,
  );
  const vlPatrimLiq = normalizeOptionalNonNegative(parent.vlPatrimLiq);
  const top1 = normalizePointNumbers(cards.top1);
  const top5 = normalizePointNumbers(cards.top5);
  const top10 = normalizePointNumbers(cards.top10);
  const top25 = normalizePointNumbers(cards.top25);
  const normalizedPositions: FicFidcCockpitConcentrationV1Position[] = [];
  const normalizedMarkers: FicFidcCockpitConcentrationV1Point[] = [];

  if (
    fundCount === null ||
    eligibleFundCount === null ||
    missingPositionCount === null ||
    vlPatrimLiq === undefined
  ) {
    return null;
  }

  if (top1 === null || top5 === null || top10 === null || top25 === null) {
    return null;
  }

  for (const position of payload.data) {
    const normalizedPosition = normalizePositionNumbers(position);

    if (normalizedPosition === null) return null;

    normalizedPositions.push(normalizedPosition);
  }

  for (const marker of markers) {
    const normalizedMarker = normalizePointNumbers(marker);

    if (normalizedMarker === null) return null;

    normalizedMarkers.push(normalizedMarker);
  }

  return {
    ...payload,
    data: normalizedPositions,
    meta: {
      ...metaRest,
      parent: { ...parent, vlPatrimLiq },
      coverage: { fundCount, eligibleFundCount, missingPositionCount },
      cards: { top1, top5, top10, top25 },
      markers: normalizedMarkers,
    },
  } as FicFidcCockpitConcentrationV1Payload;
}

function isPointShapeValid(
  point: unknown,
): point is FicFidcCockpitConcentrationV1Point {
  if (point === null || typeof point !== 'object') return false;

  const {
    top,
    available,
    rank,
    cumulativeVlMercPosFinal,
    cumulativePercentage,
  } = point as Partial<
    Record<
      | 'top'
      | 'available'
      | 'rank'
      | 'cumulativeVlMercPosFinal'
      | 'cumulativePercentage',
      unknown
    >
  >;

  if (!(CONCENTRATION_RANKS as readonly number[]).includes(Number(top))) {
    return false;
  }

  if (typeof available !== 'boolean') return false;

  return available
    ? isNonNegativeInteger(rank) &&
        toNonNegativeNumber(cumulativeVlMercPosFinal) !== null &&
        toNonNegativeNumber(cumulativePercentage) !== null
    : rank === null &&
        cumulativeVlMercPosFinal === null &&
        cumulativePercentage === null;
}

function isPositionShapeValid(
  position: unknown,
  expectedRank: number,
): position is FicFidcCockpitConcentrationV1Position {
  if (position === null || typeof position !== 'object') return false;

  const {
    rank,
    name,
    cnpj,
    operationId,
    slug,
    qtPosFinal,
    vlMercPosFinal,
    percentage,
    cumulativeVlMercPosFinal,
    cumulativePercentage,
  } = position as Partial<
    Record<
      | 'rank'
      | 'name'
      | 'cnpj'
      | 'operationId'
      | 'slug'
      | 'qtPosFinal'
      | 'vlMercPosFinal'
      | 'percentage'
      | 'cumulativeVlMercPosFinal'
      | 'cumulativePercentage',
      unknown
    >
  >;

  return (
    isNonNegativeInteger(rank) &&
    rank === expectedRank &&
    (name === null || typeof name === 'string') &&
    typeof cnpj === 'string' &&
    cnpj.trim().length > 0 &&
    (operationId === null || typeof operationId === 'string') &&
    (slug === null || typeof slug === 'string') &&
    (qtPosFinal === null || toFiniteNumber(qtPosFinal) !== null) &&
    toNonNegativeNumber(vlMercPosFinal) !== null &&
    (percentage === null || toNonNegativeNumber(percentage) !== null) &&
    toNonNegativeNumber(cumulativeVlMercPosFinal) !== null &&
    (cumulativePercentage === null ||
      toNonNegativeNumber(cumulativePercentage) !== null)
  );
}

function isValidPayloadShape(
  payload: FicFidcCockpitConcentrationV1Payload,
): boolean {
  /**
   * Payload de rede pode vir malformado (mudança de contrato, erro de
   * serialização): valide `payload`/`meta` antes de qualquer
   * desestruturação ou acesso, para degradar para `unavailable` sem lançar.
   */
  if (
    payload === null ||
    typeof payload !== 'object' ||
    payload.meta === null ||
    typeof payload.meta !== 'object'
  ) {
    return false;
  }

  const { meta } = payload;

  const grouped = meta.grouped;

  if (
    grouped !== undefined &&
    (grouped === null ||
      typeof grouped !== 'object' ||
      !isGroupedCompositionValid(grouped.ativoLastro) ||
      !isGroupedCompositionValid(grouped.gestor))
  ) {
    return false;
  }

  return (
    payload.version === FIC_FIDC_COCKPIT_CONCENTRATION_VERSION &&
    Array.isArray(payload.data) &&
    payload.data.every((position, index) =>
      isPositionShapeValid(position, index + 1),
    ) &&
    KNOWN_STATUSES.includes(meta.status) &&
    KNOWN_REASONS.includes(meta.reason) &&
    (meta.requestedCompetence === null ||
      typeof meta.requestedCompetence === 'string') &&
    (meta.competence === null || typeof meta.competence === 'string') &&
    meta.parent !== null &&
    typeof meta.parent === 'object' &&
    (meta.parent.cnpj === null || typeof meta.parent.cnpj === 'string') &&
    (meta.parent.vlPatrimLiq === null ||
      toNonNegativeNumber(meta.parent.vlPatrimLiq) !== null) &&
    meta.coverage !== null &&
    typeof meta.coverage === 'object' &&
    isNonNegativeInteger(meta.coverage.fundCount) &&
    isNonNegativeInteger(meta.coverage.eligibleFundCount) &&
    isNonNegativeInteger(meta.coverage.missingPositionCount) &&
    isPointShapeValid(meta.cards?.top1) &&
    isPointShapeValid(meta.cards?.top5) &&
    isPointShapeValid(meta.cards?.top10) &&
    isPointShapeValid(meta.cards?.top25) &&
    Array.isArray(meta.markers) &&
    meta.markers.every(isPointShapeValid)
  );
}

function unavailableResult(): FicFidcCockpitConcentrationData {
  return {
    status: 'unavailable',
    reason: 'none',
    competence: null,
    totalFunds: null,
    totalValue: null,
    parentCnpj: null,
    missingPositionCount: null,
    ranks: CONCENTRATION_RANKS.map((rank) => ({
      rank,
      value: null,
      percentage: null,
      isAvailable: false,
      isMissingPosition: false,
    })),
    funds: [],
    curve: [],
    composition: {
      asset: unavailableCompositionCard('asset'),
      manager: unavailableCompositionCard('manager'),
    },
  };
}

function buildRanks(
  cards: FicFidcCockpitConcentrationV1Payload['meta']['cards'],
  eligibleFundCount: number | null,
): FicFidcCockpitConcentrationRankEntry[] {
  const cardByRank = new Map<number, FicFidcCockpitConcentrationV1Point>([
    [cards.top1.top, cards.top1],
    [cards.top5.top, cards.top5],
    [cards.top10.top, cards.top10],
    [cards.top25.top, cards.top25],
  ]);

  return CONCENTRATION_RANKS.map((rank) => {
    const card = cardByRank.get(rank);

    if (!card) {
      return {
        rank,
        value: null,
        percentage: null,
        isAvailable: false,
        isMissingPosition: false,
      };
    }

    return {
      rank,
      value: card.cumulativeVlMercPosFinal,
      percentage: card.cumulativePercentage,
      isAvailable: card.available,
      isMissingPosition:
        !card.available &&
        eligibleFundCount !== null &&
        rank > eligibleFundCount,
    };
  });
}

/**
 * Fatias do gráfico resumido: primeiras posições nomeadas + agregado "Outros".
 * A agregação soma valores e porcentagens JÁ reportados pelo backend — não
 * recalcula percentual, não divide por denominador próprio e não faz fetch
 * por fundo.
 */
function buildFundSlices(
  positions: FicFidcCockpitConcentrationV1Position[],
): FicFidcCockpitConcentrationFundSlice[] {
  const namedSlices = positions
    .slice(0, FUND_CHART_SLICE_LIMIT)
    .map((position) => ({
      fundName: position.name?.trim() || 'FIDC sem nome',
      value: position.vlMercPosFinal,
      percentage: position.percentage ?? 0,
    }));

  const otherPositions = positions.slice(FUND_CHART_SLICE_LIMIT);

  if (!otherPositions.length) {
    return namedSlices;
  }

  const otherSlice = otherPositions.reduce(
    (accumulator, position) => ({
      fundName: OTHER_SLICE_NAME,
      value: accumulator.value + position.vlMercPosFinal,
      percentage: accumulator.percentage + (position.percentage ?? 0),
    }),
    { fundName: OTHER_SLICE_NAME, value: 0, percentage: 0 },
  );

  return [...namedSlices, otherSlice];
}

/**
 * Pontos da curva acumulada: valor e percentual acumulados JÁ reportados pelo
 * backend para cada posição elegível — não recalcula nem agrega valores.
 */
function buildCurvePoints(
  positions: FicFidcCockpitConcentrationV1Position[],
): FicFidcCockpitConcentrationCurvePoint[] {
  return positions.flatMap((position) =>
    position.cumulativePercentage === null
      ? []
      : [
          {
            rank: position.rank,
            cumulativeVlMercPosFinal: position.cumulativeVlMercPosFinal,
            cumulativePercentage: position.cumulativePercentage,
          },
        ],
  );
}

/**
 * Mapeia o payload v1 (UQ-4530) para o view-model dos widgets de
 * concentração. Não recalcula participação nem agrega por conta própria:
 * normaliza os campos numéricos no limite do mapper (números serializados
 * como string permanecem válidos), valida o contrato (versão, eco da
 * competência solicitada em `meta.requestedCompetence`, formas dos valores)
 * e degrada para `unavailable` quando algo não bate, sem fallback silencioso.
 * A competência efetiva (`meta.competence`) pode ser null em
 * `empty/no-competence-data` — isso é um estado legítimo, não um mismatch.
 */
export function mapFicFidcCockpitConcentration(
  payload: FicFidcCockpitConcentrationV1Payload | null | undefined,
  expectedCompetencyDate: string,
): FicFidcCockpitConcentrationData {
  const normalizedPayload = normalizePayloadNumbers(payload);

  if (
    !normalizedPayload ||
    !isValidPayloadShape(normalizedPayload) ||
    normalizedPayload.meta.requestedCompetence !== expectedCompetencyDate
  ) {
    return unavailableResult();
  }

  const { meta } = normalizedPayload;
  const eligibleFundCount = meta.coverage.eligibleFundCount;

  let status: FicFidcCockpitConcentrationStatus;

  if (meta.status === 'error') {
    status = 'error';
  } else if (meta.status === 'available') {
    status = 'ready';
  } else if (meta.status === 'partial') {
    status = 'partial';
  } else if (meta.status === 'empty') {
    status =
      meta.reason === 'no-competence-data' ? 'no-competence-data' : 'empty';
  } else if (meta.reason === 'zero-total') {
    status = 'zero-total';
  } else {
    status = 'unavailable';
  }

  return {
    status,
    reason: meta.reason,
    competence: meta.competence,
    totalFunds: eligibleFundCount,
    totalValue: meta.parent.vlPatrimLiq,
    parentCnpj: meta.parent.cnpj,
    missingPositionCount: meta.coverage.missingPositionCount,
    ranks: buildRanks(meta.cards, eligibleFundCount),
    funds: UNAVAILABLE_RESULT_STATUSES.has(status)
      ? []
      : buildFundSlices(normalizedPayload.data),
    curve: UNAVAILABLE_RESULT_STATUSES.has(status)
      ? []
      : buildCurvePoints(normalizedPayload.data),
    composition: {
      asset: mapGroupedComposition('asset', meta.grouped, meta.competence),
      manager: mapGroupedComposition('manager', meta.grouped, meta.competence),
    },
  };
}
